import { Slot, Slottable } from '@radix-ui/react-slot';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn, disabledStyles, focusRing } from './utils';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-outline'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-brand-contrast border border-transparent hover:bg-brand-hover active:bg-brand-active shadow-sm',
  secondary:
    'bg-surface text-content border border-border hover:bg-surface-sunken active:bg-surface-sunken',
  ghost: 'bg-transparent text-content border border-transparent hover:bg-surface-sunken',
  danger:
    'bg-danger text-danger-contrast border border-transparent hover:bg-danger-hover shadow-sm',
  'danger-outline':
    'bg-transparent text-danger-content border border-danger hover:bg-danger-subtle',
  link: 'bg-transparent text-brand border border-transparent underline underline-offset-2 hover:text-brand-hover p-0 h-auto',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-[2.375rem] px-4 text-base gap-2',
  lg: 'h-11 px-5 text-base gap-2',
  icon: 'h-[2.375rem] w-[2.375rem] p-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Renderiza o filho no lugar do `<button>` (para `<a>` de navegação). */
  readonly asChild?: boolean;
  readonly loading?: boolean;
  /** Texto anunciado durante o carregamento. Padrão: "Processando…". */
  readonly loadingLabel?: string;
  readonly leadingIcon?: ReactNode;
  readonly trailingIcon?: ReactNode;
  /**
   * Obrigatório quando `size="icon"`: um botão só com ícone precisa de nome
   * acessível, senão o leitor de tela anuncia apenas "botão".
   */
  readonly iconLabel?: string;
}

/**
 * Botão do design system.
 *
 * Decisões de acessibilidade:
 *  · o estado de carregamento NÃO troca o rótulo visível de lugar — o texto
 *    permanece e um `role="status"` invisível anuncia o progresso, evitando que
 *    o leitor de tela perca o contexto do botão que o usuário acabou de acionar;
 *  · `aria-busy` marca o elemento como ocupado em vez de removê-lo do fluxo;
 *  · o botão fica realmente `disabled` durante o envio, porque um duplo clique
 *    num ato assinado geraria duas tentativas de consumir a mesma prova.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    asChild = false,
    loading = false,
    loadingLabel = 'Processando…',
    leadingIcon,
    trailingIcon,
    iconLabel,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const Component = asChild ? Slot : 'button';
  const isDisabled = disabled === true || loading;

  /**
   * Com `asChild`, o `Slot` do Radix exige UM único elemento filho — ele funde
   * as props nele. Passar ícone + filho é dois elementos, e a resposta é a
   * exceção `Slot failed to slot onto its children`, que sobe até o
   * ErrorBoundary e **derruba a tela inteira**: a Lista Mestra, o detalhe do
   * documento, a fila de aprovações e o painel de administração ficavam com
   * "Algo deu errado nesta tela" no lugar do conteúdo, e todo botão de navegação
   * com ícone caía no mesmo caminho (18 usos de `asChild`, 7 com ícone).
   *
   * `Slottable` é a saída que o próprio Radix oferece: marca QUAL filho recebe
   * a fusão; os demais são renderizados dentro dele. Assim o ícone continua
   * aparecendo e o `<a>` continua sendo o elemento real.
   */
  const label = asChild ? <Slottable>{children}</Slottable> : children;

  return (
    <Component
      ref={ref}
      type={asChild ? undefined : type}
      disabled={asChild ? undefined : isDisabled}
      aria-disabled={asChild && isDisabled ? true : undefined}
      aria-busy={loading || undefined}
      aria-label={size === 'icon' ? iconLabel : props['aria-label']}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium',
        'transition-colors duration-100 select-none',
        focusRing,
        disabledStyles,
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : leadingIcon}
      {/*
        O filho é SEMPRE renderizado.

        Havia aqui um `size === 'icon' ? null : children`, com a intenção de
        impedir que texto aparecesse ao lado de um botão só de ícone — o nome
        acessível vem de `iconLabel`. O efeito real foi outro: DOZE dos dezesseis
        botões de ícone da aplicação passam o ícone como FILHO, não como
        `leadingIcon`, e todos renderizavam uma caixa vazia. Some a seta da
        paginação, o "x" que remove um destinatário, o menu sanduíche, o
        alternador de tema, as setas de reordenar tipo e o botão de baixar
        arquivo — controles sem nada dentro, com nome acessível correto para o
        leitor de tela e invisíveis para quem enxerga.

        Com `asChild` o filho também é obrigatório: ele é o elemento que recebe
        a fusão de props do `Slot`, e descartá-lo derrubava a tela inteira.
      */}
      {label}
      {loading ? (
        <span role="status" className="qc-sr-only">
          {loadingLabel}
        </span>
      ) : null}
      {!loading && trailingIcon}
    </Component>
  );
});

function Spinner() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="animate-spin shrink-0"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" fill="none" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Agrupador de botões com espaçamento consistente. */
export function ButtonGroup({
  children,
  className,
  align = 'end',
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly align?: 'start' | 'end' | 'between';
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        align === 'end' && 'justify-end',
        align === 'between' && 'justify-between',
        className,
      )}
    >
      {children}
    </div>
  );
}
